import { useQuery } from '@tanstack/react-query';
import { Factory, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CRITICALITIES, MACHINE_STATUSES } from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Card,
  EmptyState,
  Input,
  ListRow,
  Select,
  SkeletonRows,
  StatusPill,
  severityTone,
} from '../../components/ui';
import { api, qs } from '../../lib/api';
import { humanise } from '../../lib/format';

interface MachineRow {
  id: string;
  code: string;
  name: string;
  machineType: string | null;
  status: string;
  criticality: string;
  lineName: string;
  areaName: string;
  factoryName: string;
}

export function MachineListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') ?? '');

  const status = params.get('status') ?? '';
  const criticality = params.get('criticality') ?? '';
  const lineId = params.get('lineId') ?? '';

  const { data, isLoading } = useQuery({
    queryKey: ['machines', { search, status, criticality, lineId }],
    queryFn: () =>
      api.get<{ data: MachineRow[] }>(
        `/api/machines${qs({ search, status, criticality, lineId })}`,
      ),
  });

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const grouped = (data?.data ?? []).reduce<Record<string, MachineRow[]>>((acc, machine) => {
    (acc[machine.lineName] ??= []).push(machine);
    return acc;
  }, {});

  return (
    <div>
      <PageHeader title="Machines" subtitle="Equipment in the AM/ODR pilot" />

      <div className="space-y-4 p-4 lg:p-6">
        <Card className="p-3">
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="relative sm:col-span-1">
              <Search
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-steel-400"
              />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Machine ID or name"
                aria-label="Search machines"
                className="pl-9"
              />
            </div>
            <Select
              value={status}
              onChange={(event) => setParam('status', event.target.value)}
              aria-label="Filter by status"
            >
              <option value="">Any status</option>
              {MACHINE_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </Select>
            <Select
              value={criticality}
              onChange={(event) => setParam('criticality', event.target.value)}
              aria-label="Filter by criticality"
            >
              <option value="">Any criticality</option>
              {CRITICALITIES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </Select>
          </div>
        </Card>

        {isLoading ? (
          <Card>
            <SkeletonRows rows={5} />
          </Card>
        ) : Object.keys(grouped).length === 0 ? (
          <Card>
            <EmptyState
              icon={Factory}
              title="No machines match"
              message="Try clearing the filters, or check the machine ID you searched for."
            />
          </Card>
        ) : (
          Object.entries(grouped).map(([line, machines]) => (
            <Card key={line}>
              <p className="border-b border-steel-200 bg-steel-50 px-4 py-2 text-xs font-extrabold tracking-widest text-steel-600 uppercase">
                {line}
              </p>
              <ul className="divide-y divide-steel-100">
                {machines.map((machine) => (
                  <li key={machine.id}>
                    <ListRow onClick={() => navigate(`/machines/${machine.id}`)}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-steel-900">
                            {machine.code} · {machine.name}
                          </p>
                          <p className="text-xs text-steel-500">
                            {machine.machineType ?? 'Machine'} · {machine.areaName}
                          </p>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <StatusPill
                            tone={
                              machine.status === 'RUNNING'
                                ? 'ok'
                                : machine.status === 'MAINTENANCE'
                                  ? 'warn'
                                  : 'neutral'
                            }
                            label={humanise(machine.status)}
                          />
                          <StatusPill
                            tone={severityTone(machine.criticality)}
                            label={humanise(machine.criticality)}
                          />
                        </div>
                      </div>
                    </ListRow>
                  </li>
                ))}
              </ul>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
